import { putApiChatEvaluationTestRename, useGetApiChatEvaluationTestList, usePutApiChatEvaluationTestDelete, usePutApiChatEvaluationTestRename } from "@/API"
import { TimestampToDate } from "@/Helpers/DateHelper"
import { ActionIcon, Box, Button, Group, Pagination, Text, Tooltip } from "@mantine/core"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "@tanstack/react-router"
import { DataTable } from "mantine-datatable"
import { useState } from "react"
import { EvaluateTestSetJobManagerButton } from "./EvaluateTestSetJobManager"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { IconEdit, IconFlame, IconRefresh, IconTrash } from "@tabler/icons-react"
import { PageTitle } from "@/Components/PageTitle"

export function ChatEvaluationTestList() {
    const { workspaceId, chatEvaluationTestSetId } = useParams({ from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/" })
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const PAGE_SIZE = 5
    const [activePage, setPage] = useState(1);
    const testData = useGetApiChatEvaluationTestList({ workspaceId: workspaceId, testSetId: chatEvaluationTestSetId, skip: (activePage - 1) * PAGE_SIZE, limit: PAGE_SIZE })

    const results = testData.data?.data.results ?? []

    const rename_Mutation = usePutApiChatEvaluationTestRename()
    const delete_Mutation = usePutApiChatEvaluationTestDelete()

    return <>
        <Group mb="md" gap="sm">
            <PageTitle text="評估與執行紀錄" />
            <ActionIcon variant="subtle" onClick={() => {
                queryClient.invalidateQueries({ queryKey: testData.queryKey })
            }}>
                <IconRefresh style={{ width: '70%', height: '70%' }} />
            </ActionIcon>
            <EvaluateTestSetJobManagerButton workspaceId={workspaceId} testSetId={chatEvaluationTestSetId}>
                <Button leftSection={<IconFlame />}>
                    評估工作管理
                </Button>
            </EvaluateTestSetJobManagerButton>
        </Group>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil((results.length ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={results.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            fetching={testData.isFetching}
            columns={[
                { accessor: 'name', title: "名稱" },
                { accessor: "createdTimestamp", width: 200, title: "創建時間", render: (data) => (TimestampToDate(data.createdTimestamp ?? 0)).toLocaleString() },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <Tooltip label="修改名稱">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "TextInput",
                                            title: '修改名稱',
                                            innerProps: {
                                                initText: data.name ?? "",
                                                onConfirm: async (v) => {
                                                    try {
                                                        if (data.id) {
                                                            await rename_Mutation.mutateAsync(
                                                                {
                                                                    params: {
                                                                        workspaceId: workspaceId,
                                                                        id: data.id,
                                                                        name: v
                                                                    }
                                                                })
                                                            queryClient.invalidateQueries({ queryKey: testData.queryKey })
                                                            notifications.show({ message: "修改成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "修改失敗", color: "red" })
                                                    }
                                                },
                                            },

                                        });
                                    }}
                                >
                                    <IconEdit size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="刪除紀錄">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "ConfirmCountDown",
                                            title: '刪除',
                                            innerProps: {
                                                countDownSecond: 0,
                                                children: (
                                                    <Text size="sm">
                                                        是否要刪除"{data.id}"?
                                                    </Text>
                                                ),
                                                labels: {
                                                    confirm: '刪除', cancel: '取消'
                                                },
                                                confirmProps: { color: 'red' },
                                                onConfirm: async () => {
                                                    try {
                                                        if (data.id) {
                                                            await delete_Mutation.mutateAsync({
                                                                params: {
                                                                    workspaceId: workspaceId,
                                                                    id: data.id
                                                                }
                                                            })
                                                            queryClient.invalidateQueries({ queryKey: testData.queryKey })
                                                            notifications.show({ message: "刪除成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "刪除失敗", color: "red" })
                                                    }
                                                },
                                            },

                                        });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    ),
                },
            ]}
            onRowDoubleClick={({ record }) => {
                navigate({
                    to: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId", params: {
                        workspaceId: workspaceId!,
                        chatEvaluationTestSetId: record.testSetId!,
                        chatEvaluationTestId: record.id!,
                    }
                })
            }}
            records={results}
        />
    </>
}
